import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import HeaderNavTrigger from "../Header/HeaderNavTrigger";
import { get_ApiManager } from "../ApiManager/ApiManager";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import DOMPurify from "dompurify";
import { FaUserCircle, FaClock, FaFileAlt } from "react-icons/fa";
import { Inbox as InboxIcon } from "lucide-react";
import HeaderPanelModal, {
  PanelEmptyState,
  PanelListSkeleton,
  panelBadgeTw,
  formatRelativeTime,
  MessagingSplitGrid,
  MessagingListColumn,
  MessagingListToolbar,
  MessagingDetailColumn,
  messagingListCardClass,
} from "../Header/HeaderPanelModal";

const isSuccessResponse = (res) =>
  res?.data?.status === true || res?.data?.success === true;

const resolveClientId = (main) =>
  main?.clientid ?? main?.client_id ?? main?.client?.id ?? null;

const resolveAppId = (main) => main?.appid ?? main?.app_id ?? null;

const resolveAppType = (main) => main?.apptype ?? main?.app_type ?? null;

const getInboxMessageMeta = (item) => {
  if (!item) {
    return { label: "Message", badgeTw: panelBadgeTw.secondary };
  }

  const appId = resolveAppId(item);
  const appType = resolveAppType(item);
  const appTypeKey = String(appType ?? "").toLowerCase();
  const appIdNum = Number(appId);

  if (
    appId &&
    appIdNum !== 0 &&
    (appTypeKey === "edu" || appTypeKey === "education")
  ) {
    return {
      label: "Education application",
      badgeTw: panelBadgeTw.info,
    };
  }

  if (
    appId &&
    appIdNum !== 0 &&
    (appTypeKey === "mig" || appTypeKey === "migration")
  ) {
    return {
      label: "Migration application",
      badgeTw: panelBadgeTw.warning,
    };
  }

  if (
    (!appId || appIdNum === 0) &&
    (appType === 0 || appType === "0" || appTypeKey === "0")
  ) {
    return {
      label: "Client message",
      badgeTw: panelBadgeTw.primary,
    };
  }

  return {
    label: "Client message",
    badgeTw: panelBadgeTw.primary,
  };
};

const getInboxActionLink = (main) => {
  if (!main) return null;

  const clientId = resolveClientId(main);
  const appId = resolveAppId(main);
  const appType = resolveAppType(main);
  const appTypeKey = String(appType ?? "").toLowerCase();
  const appIdNum = Number(appId);

  if (
    appId &&
    appIdNum !== 0 &&
    (appTypeKey === "edu" || appTypeKey === "education")
  ) {
    return {
      to: `/applications/view-education-application-detail/${appId}`,
      label: "Open application",
      icon: "file",
    };
  }

  if (
    appId &&
    appIdNum !== 0 &&
    (appTypeKey === "mig" || appTypeKey === "migration")
  ) {
    return {
      to: `/applications/view-migration-application-detail/${appId}`,
      label: "Open application",
      icon: "file",
    };
  }

  if (
    (!appId || appIdNum === 0) &&
    (appType === 0 || appType === "0" || appTypeKey === "0")
  ) {
    if (!clientId) return null;
    return {
      to: `/clients/view-client-detail/${clientId}`,
      label: "Open client profile",
      icon: "user",
    };
  }

  if (clientId) {
    return {
      to: `/clients/view-client-detail/${clientId}`,
      label: "Open client profile",
      icon: "user",
    };
  }

  return null;
};

const sortThreads = (rows) => {
  const list = [...rows];
  list.sort((a, b) => {
    const unreadA = a.is_read === 0 ? 0 : 1;
    const unreadB = b.is_read === 0 ? 0 : 1;
    if (unreadA !== unreadB) return unreadA - unreadB;
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    return tb - ta;
  });
  return list;
};

const Inbox = (props) => {
  const ref = useRef();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [detailMain, setDetailMain] = useState(null);
  const [detailChat, setDetailChat] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const unreadBadge = useMemo(() => {
    const seen = props.inboxSeen;
    if (!seen || typeof seen !== "object") return 0;
    const raw =
      seen.unreadCount ?? seen.unread_count ?? seen.unread ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, [props.inboxSeen]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () =>
      document.removeEventListener("mousedown", checkIfClickedOutside);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedThread(null);
      setDetailMain(null);
      setDetailChat([]);
    }
  }, [open]);

  const fetchAllClientThreads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiUrl.get(`getClientChat`);

      if (!isSuccessResponse(res)) {
        setThreads([]);
        return;
      }

      const rows = Array.isArray(res.data.data) ? res.data.data : [];
      const sorted = sortThreads(rows);
      setThreads(sorted);
      if (sorted.length > 0) {
        setSelectedThread((prev) => {
          if (prev && sorted.some((t) => t.id === prev.id)) {
            return sorted.find((t) => t.id === prev.id) ?? prev;
          }
          const firstUnread = sorted.find((t) => t.is_read === 0);
          return firstUnread ?? sorted[0];
        });
      } else {
        setSelectedThread(null);
      }
    } catch (error) {
      console.error(error);
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const emitChatRefresh = useCallback(() => {
    const s = props.socket;
    if (s) s.emit("sendChatNotification");
  }, [props.socket]);

  const markThreadReadApi = useCallback(
    (threadId, wasUnread) => {
      get_ApiManager(
        "",
        `markReadCms/${threadId}/client`,
        () => {
          setThreads((prev) =>
            prev.map((t) => (t.id === threadId ? { ...t, is_read: 1 } : t)),
          );
          setSelectedThread((prev) =>
            prev && prev.id === threadId ? { ...prev, is_read: 1 } : prev,
          );
          emitChatRefresh();
          if (wasUnread && typeof props.onRefreshInboxCount === "function") {
            props.onRefreshInboxCount();
          }
        },
        () => {},
      );
    },
    [emitChatRefresh, props.onRefreshInboxCount],
  );

  useEffect(() => {
    if (!open || !selectedThread?.id) {
      setDetailMain(null);
      setDetailChat([]);
      setDetailLoading(false);
      return;
    }

    setDetailLoading(true);
    get_ApiManager(
      "",
      `getClientChatHistory/${selectedThread.id}`,
      (res) => {
        setDetailLoading(false);
        const main = res?.data?.data?.main ?? null;
        const chat = res?.data?.data?.chat ?? [];
        setDetailMain(main);
        setDetailChat(Array.isArray(chat) ? chat : []);
        const wasUnread = Number(selectedThread.is_read) !== 1;
        markThreadReadApi(selectedThread.id, wasUnread);
      },
      () => {
        setDetailLoading(false);
      },
    );
  }, [open, selectedThread?.id, markThreadReadApi]);

  const handleIconClick = () => {
    if (!open) {
      setOpen(true);
      fetchAllClientThreads();
    } else {
      setOpen(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const inboxActionLink = useMemo(
    () => getInboxActionLink(detailMain),
    [detailMain],
  );

  const selectedMessageMeta = useMemo(
    () => getInboxMessageMeta(detailMain ?? selectedThread),
    [detailMain, selectedThread],
  );

  const messagePreviewHtml = useMemo(() => {
    if (!detailChat.length && !detailMain) return "";
    const last = detailChat[detailChat.length - 1];
    const raw = last?.body || "";
    if (raw) return DOMPurify.sanitize(raw);
    return DOMPurify.sanitize(detailMain?.subject || "");
  }, [detailChat, detailMain]);

  return (
    <>
      <HeaderNavTrigger
        label="Inbox"
        onClick={handleIconClick}
        badgeCount={unreadBadge}
      >
        {props.icon}
      </HeaderNavTrigger>

      <HeaderPanelModal
        open={open}
        onClose={() => setOpen(false)}
        title="Client messages"
        subtitle={
          threads.length > 0
            ? `${threads.length} conversation${threads.length === 1 ? "" : "s"}`
            : "Client conversations"
        }
        headerIcon={
          <InboxIcon size={18} strokeWidth={2} className="text-white" />
        }
        panelRef={ref}
        ariaLabelledBy="header-inbox-title"
      >
        <MessagingSplitGrid>
          <MessagingListColumn
            toolbar={
              <MessagingListToolbar
                title="Conversations"
                trailing={
                  unreadBadge > 0 ? (
                    <span
                      className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white"
                    >
                      {unreadBadge} unread
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-400">All read</span>
                  )
                }
              />
            }
          >
            {loading ? (
              <PanelListSkeleton rows={5} />
            ) : threads.length === 0 ? (
              <PanelEmptyState
                icon={<InboxIcon size={28} strokeWidth={1.75} />}
                title="No client messages"
                description="You're all caught up — no client conversations yet."
              />
            ) : (
              <ul className="m-0 list-none space-y-1.5 p-0">
                {threads.map((thread) => {
                  const threadMeta = getInboxMessageMeta(thread);
                  const isActive = selectedThread?.id === thread.id;
                  const isUnread = thread.is_read === 0;
                  return (
                    <li key={thread.id}>
                      <button
                        type="button"
                        className={messagingListCardClass(isActive, isUnread)}
                        onClick={() => setSelectedThread(thread)}
                      >
                        <div className="flex gap-3">
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                              isUnread ? "bg-blue-100" : "bg-slate-200/70"
                            }`}
                          >
                            <FaUserCircle className="text-lg text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1 text-left">
                            <div className="flex items-start gap-2">
                              <p
                                className={`mb-0 line-clamp-2 flex-1 text-[13px] leading-snug ${
                                  isUnread
                                    ? "font-semibold text-slate-900"
                                    : "font-medium text-slate-600"
                                }`}
                              >
                                {thread.subject || "Message"}
                              </p>
                              {isUnread ? (
                                <span
                                  className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600"
                                  aria-hidden
                                />
                              ) : null}
                            </div>
                            <div
                              className="mt-1.5 flex flex-wrap items-center gap-1.5"
                            >
                              <span className={threadMeta.badgeTw}>
                                {threadMeta.label}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                · {formatRelativeTime(thread.created_at)}
                              </span>
                            </div>
                            {thread.sender ? (
                              <p
                                className="mb-0 mt-1.5 truncate text-[11px] font-medium text-blue-700"
                              >
                                <FaUserCircle className="mr-1 inline text-[10px]" />
                                {thread.sender}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </MessagingListColumn>

          <MessagingDetailColumn>
            {selectedThread && detailLoading ? (
              <div
                className="flex flex-1 items-center justify-center gap-2 p-8 text-sm text-slate-500"
              >
                <span
                  className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600"
                  role="status"
                  aria-label="Loading"
                />
                Loading conversation…
              </div>
            ) : selectedThread && detailMain ? (
              <>
                <div
                  className="shrink-0 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white px-5 py-4"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50"
                    >
                      <FaUserCircle className="text-2xl text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start gap-2">
                        <h3
                          className="mb-0 flex-1 text-base font-semibold leading-snug text-slate-900"
                        >
                          {detailMain.subject || selectedThread.subject}
                        </h3>
                        {selectedThread.is_read === 0 ? (
                          <span
                            className="shrink-0 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                          >
                            Unread
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500"
                      >
                        <span className="inline-flex items-center gap-1">
                          <FaClock className="opacity-70" />
                          {formatDate(
                            detailMain.created_at ||
                              selectedThread.created_at,
                          )}
                        </span>
                        <span className={selectedMessageMeta.badgeTw}>
                          {selectedMessageMeta.label}
                        </span>
                        {selectedThread.sender ? (
                          <span className="font-medium text-blue-700">
                            {selectedThread.sender}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cms-panel-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                  <p
                    className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400"
                  >
                    Message
                  </p>
                  <div
                    className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3.5 text-sm leading-relaxed text-slate-700 [&_a]:text-blue-700 [&_a]:underline"
                  >
                    {messagePreviewHtml ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: messagePreviewHtml,
                        }}
                      />
                    ) : (
                      <span className="text-slate-400">
                        No message body to preview.
                      </span>
                    )}
                  </div>
                </div>

                {inboxActionLink ? (
                  <div
                    className="flex shrink-0 flex-wrap gap-2 border-t border-slate-200 bg-slate-50/80 px-5 py-3"
                  >
                    <Link
                      to={inboxActionLink.to}
                      className="inline-flex items-center gap-2 rounded-lg bg-[#0b2166] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#1a3a8a]"
                      onClick={() => setOpen(false)}
                    >
                      {inboxActionLink.icon === "file" ? (
                        <FaFileAlt className="text-[11px]" />
                      ) : (
                        <FaUserCircle className="text-[11px]" />
                      )}
                      {inboxActionLink.label}
                    </Link>
                  </div>
                ) : null}
              </>
            ) : selectedThread && !detailLoading ? (
              <div className="flex flex-1 items-center justify-center p-6 text-sm text-slate-500">
                Could not load this conversation.
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-6">
                <PanelEmptyState
                  icon={<InboxIcon size={22} strokeWidth={1.75} />}
                  title="Select a conversation"
                  description="Pick a message from the list to read the full content"
                />
              </div>
            )}
          </MessagingDetailColumn>
        </MessagingSplitGrid>
      </HeaderPanelModal>
    </>
  );
};

export default Inbox;
